import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ArticleCard, FeatureCard } from "@/components/site/ArticleCard";
import { listCategories, listPublishedArticles } from "@/lib/articles.functions";
import { SITE } from "@/lib/site";

const CATEGORY_TO_GUIDE: Record<string, { slug: string; label: string }> = {
  "google-business-profile": { slug: "google-business-profile", label: "Google Business Profile, explained" },
  "local-rankings": { slug: "map-pack", label: "What is the Map Pack?" },
  "google-maps": { slug: "local-seo", label: "Local SEO in 2026" },
  "ai-search": { slug: "ai-search-local", label: "AI search for local" },
  "reviews-reputation": { slug: "google-business-profile", label: "Google Business Profile, explained" },
  "industry-news": { slug: "local-seo", label: "Local SEO in 2026" },
};

export const Route = createFileRoute("/category/$slug")({
  loader: async ({ params, context }) => {
    const result = await listPublishedArticles({
      data: { categorySlug: params.slug, limit: 60 },
    });
    if (!result.category) throw notFound();
    await Promise.all([
      context.queryClient.prefetchQuery({
        queryKey: ["category", params.slug],
        queryFn: () => Promise.resolve(result),
      }),
      context.queryClient.prefetchQuery({
        queryKey: ["categories"],
        queryFn: () => listCategories(),
      }),
    ]);
    return result;
  },
  head: ({ loaderData, params }) => {
    const cat = loaderData?.category;
    const fallbackDesc: Record<string, string> = {
      "google-business-profile":
        "Google Business Profile news, policy changes, and suspension coverage for local SEO agencies, GBP specialists, and multi-location operators.",
      "local-rankings":
        "Map Pack ranking factor tests, case studies, and analysis for local SEO agencies and the operators they serve.",
      "google-maps":
        "Google Maps product and platform updates that move the needle for local businesses and the agencies optimizing them.",
      "ai-search":
        "How AI Overviews, AI Mode, and Ask Maps are reshaping local search — coverage for SEO agencies and local business owners.",
      "reviews-reputation":
        "Reviews policy, spam fighting, and reputation management for Google Business Profile — for agencies and operators.",
      "industry-news":
        "Local SEO industry news for agencies, GBP specialists, and the businesses that depend on showing up on the map.",
    };
    const title = cat
      ? `${cat.name} — Local SEO News | ${SITE.name}`
      : SITE.name;
    const desc =
      cat?.description || fallbackDesc[params.slug] || `${cat?.name ?? "Local SEO"} coverage from ${SITE.name} for agencies and local business owners.`;
    const url = `${SITE.url}/category/${params.slug}`;
    const articles = loaderData?.articles ?? [];
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: cat ? `${cat.name} | ${SITE.name}` : SITE.name },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: cat ? `${cat.name} | ${SITE.name}` : SITE.name },
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: cat
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                name: cat.name,
                description: desc,
                url,
                inLanguage: "en",
                isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
                mainEntity: {
                  "@type": "ItemList",
                  itemListElement: articles.slice(0, 20).map((a, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    url: `${SITE.url}/article/${a.slug}`,
                    name: a.title,
                  })),
                },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
                  { "@type": "ListItem", position: 2, name: cat.name, item: url },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: CategoryPage,
});

function Body() {
  const params = Route.useParams();
  const { data } = useSuspenseQuery({
    queryKey: ["category", params.slug],
    queryFn: () => listPublishedArticles({ data: { categorySlug: params.slug, limit: 60 } }),
  });
  const { data: categories } = useSuspenseQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
  });
  const catMap = new Map(categories.map((c) => [c.id, { slug: c.slug, name: c.name }] as const));
  const { articles, category } = data;
  const [hero, ...rest] = articles;

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <header className="py-12 border-b border-foreground">
        <p className="eyebrow">Section</p>
        <h1 className="headline-display mt-3 text-5xl sm:text-6xl">{category!.name}</h1>
        {category!.description && (
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{category!.description}</p>
        )}
        {CATEGORY_TO_GUIDE[category!.slug] && (
          <p className="mt-5 text-sm">
            <span className="eyebrow mr-2">New here?</span>
            <Link
              to="/guide/$slug"
              params={{ slug: CATEGORY_TO_GUIDE[category!.slug].slug }}
              className="underline decoration-accent underline-offset-4 hover:text-accent"
            >
              Read the guide: {CATEGORY_TO_GUIDE[category!.slug].label} →
            </Link>
          </p>
        )}
      </header>


      {hero && (
        <section className="py-12 border-b border-border">
          <FeatureCard article={hero} categories={catMap} />
        </section>
      )}

      {rest.length > 0 && (
        <section className="py-12 grid gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((a) => (
            <ArticleCard key={a.id} article={a} categories={catMap} />
          ))}
        </section>
      )}

      {articles.length === 0 && (
        <section className="py-24 text-center text-muted-foreground">
          No stories in this section yet. Check back soon.
        </section>
      )}
    </div>
  );
}

function CategoryPage() {
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main">
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <Body />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}
